// Canonical allergen / dish-attribute display maps.
//
// These MIRROR the kiosk/webshop source of truth (kiosk `src/graphql/public.ts`:
// ALLERGEN_LABELS, ALLERGEN_EMOJIS, ATTRIBUTE_CONFIG). The codes are a fixed enum
// shared across the API schema and every frontend — POS renders a fixed picker from
// these lists and cannot invent new codes. If a code is ever added, it must be
// coordinated across the API enum and every frontend's label/colour map, then here.

// --- Allergens (EU-14 mandatory list, in API enum order) -------------------

export const ALLERGENS = [
  'GLUTEN',
  'CRUSTACEANS',
  'EGGS',
  'FISH',
  'PEANUTS',
  'SOYBEANS',
  'DAIRY',
  'NUTS',
  'CELERY',
  'MUSTARD',
  'SESAME',
  'SULPHITES',
  'LUPIN',
  'MOLLUSCS',
] as const

export const ALLERGEN_LABELS: Record<string, string> = {
  GLUTEN: 'Gluten',
  CRUSTACEANS: 'Crustaceans',
  EGGS: 'Eggs',
  FISH: 'Fish',
  PEANUTS: 'Peanuts',
  SOYBEANS: 'Soybeans',
  DAIRY: 'Dairy',
  NUTS: 'Nuts',
  CELERY: 'Celery',
  MUSTARD: 'Mustard',
  SESAME: 'Sesame',
  SULPHITES: 'Sulphites',
  LUPIN: 'Lupin',
  MOLLUSCS: 'Molluscs',
}

export const ALLERGEN_EMOJIS: Record<string, string> = {
  GLUTEN: '🌾',
  CRUSTACEANS: '🦐',
  EGGS: '🥚',
  FISH: '🐟',
  PEANUTS: '🥜',
  SOYBEANS: '🫘',
  DAIRY: '🥛',
  NUTS: '🌰',
  CELERY: '🥬',
  MUSTARD: '🟡',
  SESAME: '🫓',
  SULPHITES: '🍷',
  LUPIN: '🌸',
  MOLLUSCS: '🐚',
}

// --- Dish attributes -------------------------------------------------------

export const ATTRIBUTE_CONFIG: Record<string, { label: string; color: string }> = {
  SPICY1: { label: 'Mild Spicy', color: 'bg-orange-100 text-orange-700' },
  SPICY2: { label: 'Spicy', color: 'bg-red-100 text-red-700' },
  SPICY3: { label: 'Extra Spicy', color: 'bg-red-200 text-red-900' },
  MILD: { label: 'Mild', color: 'bg-yellow-100 text-yellow-700' },
  VEGAN: { label: 'Vegan', color: 'bg-green-100 text-green-700' },
  VEGETARIAN: { label: 'Vegetarian', color: 'bg-green-100 text-green-700' },
  HALAL: { label: 'Halal', color: 'bg-emerald-100 text-emerald-700' },
  GLUTEN_FREE: { label: 'Gluten Free', color: 'bg-amber-100 text-amber-700' },
  DAIRY_FREE: { label: 'Dairy Free', color: 'bg-blue-100 text-blue-700' },
  NUT_FREE: { label: 'Nut Free', color: 'bg-orange-100 text-orange-700' },
  POPULAR: { label: 'Popular', color: 'bg-brand-butterscotch/20 text-brand-butterscotch' },
  NEW: { label: 'New', color: 'bg-brand-plum/10 text-brand-plum' },
  CHEFS_SPECIAL: { label: "Chef's Special", color: 'bg-brand-jasper/10 text-brand-jasper' },
}

// Attributes grouped for the editor UI. Spice is single-select (a dish has one
// spice badge); the other groups are multi-select toggles.
export interface AttributeGroup {
  key: string
  title: string
  codes: string[]
  singleSelect?: boolean
}

export const ATTRIBUTE_GROUPS: AttributeGroup[] = [
  { key: 'spice', title: 'Spice level', codes: ['SPICY1', 'SPICY2', 'SPICY3', 'MILD'], singleSelect: true },
  { key: 'dietary', title: 'Dietary', codes: ['VEGAN', 'VEGETARIAN', 'HALAL'] },
  { key: 'freeFrom', title: 'Free-from', codes: ['GLUTEN_FREE', 'DAIRY_FREE', 'NUT_FREE'] },
  { key: 'marketing', title: 'Marketing', codes: ['POPULAR', 'NEW', 'CHEFS_SPECIAL'] },
]

// All attribute codes, flat (enum order) — used for the overview filter chips.
export const ATTRIBUTES = ATTRIBUTE_GROUPS.flatMap((g) => g.codes)

export const attributeLabel = (code: string): string => ATTRIBUTE_CONFIG[code]?.label ?? code
export const attributeColor = (code: string): string =>
  ATTRIBUTE_CONFIG[code]?.color ?? 'bg-gray-100 text-gray-600'
export const allergenLabel = (code: string): string => ALLERGEN_LABELS[code] ?? code
export const allergenEmoji = (code: string): string => ALLERGEN_EMOJIS[code] ?? ''
